<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({ charge: { type: Object, required: true } });
const emit = defineEmits(['edit']);
const { t } = useI18n();

const responsibilities = computed(
  () => props.charge.dados_parcela?.responsaveis_pagamento || []
);
const roleOrder = ['Vendedor', 'Comprador'];
const groups = computed(() =>
  roleOrder
    .map(role => ({
      role,
      rows: responsibilities.value.filter(row => row.papel === role),
    }))
    .filter(group => group.rows.length)
);
const formatMoney = value => {
  const amount = Number(value?.amount);
  if (!Number.isFinite(amount)) return t('SALES.PENDING_CHARGE.EMPTY_VALUE');
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: value.currency || 'BRL',
  }).format(amount);
};
</script>

<template>
  <LaisCard
    :title="t('SALES.PENDING_CHARGE.PAYERS.TITLE')"
    prefix-icon="i-lucide-users-round"
    density="flush"
  >
    <div v-if="groups.length" class="grid gap-4 p-5 lg:grid-cols-2">
      <section
        v-for="group in groups"
        :key="group.role"
        class="overflow-hidden rounded-lais border border-lais-border-soft"
      >
        <header
          class="grid grid-cols-[1fr_auto] gap-3 border-b border-lais-border-soft bg-lais-bg-soft px-4 py-3 text-xs font-bold uppercase tracking-wide text-lais-text-muted"
        >
          <span>{{ group.role }}</span>
          <span>{{ t('SALES.PENDING_CHARGE.PAYERS.PAYMENT_VALUE') }}</span>
        </header>
        <div
          v-for="row in group.rows"
          :key="row.id"
          class="grid grid-cols-[1fr_auto] gap-3 border-b border-lais-border-soft px-4 py-3 text-sm last:border-b-0"
        >
          <span class="font-medium text-lais-text">{{ row.usuario_nome }}</span>
          <span class="text-lais-text-body">
            {{ formatMoney(row.valor_pagamento) }}
          </span>
        </div>
      </section>
    </div>
    <div
      v-else
      class="m-5 flex items-center gap-2 rounded-lais bg-amber-50 px-3 py-3 text-sm text-amber-800"
    >
      <span class="i-lucide-triangle-alert size-4" aria-hidden="true" />
      {{ t('SALES.PENDING_CHARGE.PAYERS.EMPTY') }}
    </div>
    <footer class="flex justify-end border-t border-lais-border-soft px-5 py-4">
      <LaisPrimaryButton
        variant="link"
        icon="i-lucide-pencil"
        :disabled="!charge.acoes?.pode_editar_pagadores"
        @click="emit('edit')"
      >
        {{ t('SALES.PENDING_CHARGE.PAYERS.EDIT') }}
      </LaisPrimaryButton>
    </footer>
  </LaisCard>
</template>
